import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import http from '../api/http'
import socket from '../services/socket'
import { useSessionStore } from './session'

// Líneas excluidas congeladas — no necesitan ser reactivas
const LINEAS_EXCLUIDAS = Object.freeze([4, 5, 10])

export const useMaquinaStore = defineStore('maquina', {
    state: () => ({
        datosMaquinas: [],
        loading: false,
        ultimaActualizacion: null,
        segundosRestantes: 60,
        maquinasSinGps: [],
        alertasVelocidad: [],
        alertasLegacy: [],
        _intervalId: null    // Guardamos el ID para poder limpiarlo
    }),

    getters: {
        countMaquinasSinGps: (state) => state.maquinasSinGps.length,
        countAlertasVelocidad: (state) => state.alertasVelocidad.length,
        countAlertasLegacy: (state) => state.alertasLegacy.length,
        horaUltimaActualizacion: (state) => {
            if (!state.ultimaActualizacion) return '...'
            const d = state.ultimaActualizacion
            const p = n => String(n).padStart(2, '0')
            return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
        }
    },

    actions: {
        init() {
            // Evitar doble inicialización
            if (this._intervalId) return

            const session = useSessionStore()

            // 🛡️ Optimización de Salas: Unirse según rol
            const esAdmin = session.user?.rol === 'admin'
            const esTecnicoGlobal = session.user?.rol === 'tecnico' && !session.user?.linea_id

            if (esAdmin || esTecnicoGlobal) {
                socket.emit('join_admin')
            } else if (session.user?.linea_id) {
                socket.emit('join_linea', session.user.linea_id)
            }

            // Evitar listeners duplicados si init() se invoca más de una vez
            socket.off('maquinas_update')
            socket.on('maquinas_update', (nuevosDatos) => {
                this.datosMaquinas = markRaw(this.filtrarPorRol(nuevosDatos))
                this.ultimaActualizacion = new Date()
                this.segundosRestantes = 60 // Reset tras recibir push
                this.verificarEstadoGps()
            })

            this.cargarMaquinas()

            // Guardar el intervalo para poder limpiarlo con destroy()
            this._intervalId = setInterval(() => {
                const session = useSessionStore()
                if (!session.user) {
                    this.destroy()
                    return
                }

                if (this.segundosRestantes > 0) {
                    this.segundosRestantes--
                } else {
                    this.cargarMaquinas() // Auto-refresco al llegar a 0
                }
            }, 1000)

            // Escuchar eventos emitidos por el módulo GATE
            socket.off('gate_speed_alert')
            socket.on('gate_speed_alert', (alerta) => {
                // Filtrar por línea si el usuario tiene una asignada
                const userLinea = session.user?.linea_id ? parseInt(session.user.linea_id) : null
                if (userLinea !== null && alerta.linea !== userLinea) return

                this.alertasVelocidad.unshift(alerta)
                if (this.alertasVelocidad.length > 50) {
                    this.alertasVelocidad.pop()
                }
            })

            socket.off('gate_deprecated_alert')
            socket.on('gate_deprecated_alert', (alerta) => {
                const userLinea = session.user?.linea_id ? parseInt(session.user.linea_id) : null
                if (userLinea !== null && alerta.linea !== userLinea) return

                this.alertasLegacy.unshift(alerta)
                if (this.alertasLegacy.length > 50) {
                    this.alertasLegacy.pop()
                }
            })

            socket.off('gate_gps_update')
            /* socket.on('gate_gps_update', ({ maquinaId, lineaId, telemetry }) => {
                // Actualizar la máquina en vivo dentro de datosMaquinas
            }) */
        },

        limpiarAlertasVelocidad() {
            this.alertasVelocidad = []
        },

        limpiarAlertasLegacy() {
            this.alertasLegacy = []
        },

        destroy() {
            // Llamar al desmontar el componente que usa el store
            if (this._intervalId) {
                clearInterval(this._intervalId)
                this._intervalId = null
            }
            socket.off('maquinas_update')
            socket.off('gate_speed_alert')
            socket.off('gate_deprecated_alert')
            socket.off('gate_gps_update')
        },

        async cargarMaquinas() {
            const session = useSessionStore()
            if (!session.user) return // No cargar si no hay sesión activa

            this.loading = this.datosMaquinas.length === 0
            try {
                const [resMaquinas, resSaltos, resLegacy] = await Promise.allSettled([
                    http.get('/api/maquinas'),
                    http.get('/api/maquinas/saltos-gps'),
                    http.get('/api/maquinas/alertas-legacy')
                ])

                if (resMaquinas.status === 'fulfilled') {
                    this.datosMaquinas = markRaw(this.filtrarPorRol(resMaquinas.value.data))
                }
                if (resSaltos.status === 'fulfilled' && resSaltos.value.data?.data) {
                    this.alertasVelocidad = resSaltos.value.data.data
                }
                if (resLegacy.status === 'fulfilled' && resLegacy.value.data?.data) {
                    this.alertasLegacy = resLegacy.value.data.data
                }

                this.ultimaActualizacion = new Date()
                this.segundosRestantes = 60 // Actualizar cada 30s si no llega por socket
                this.verificarEstadoGps()
            } catch (error) {
                console.error('Error cargando máquinas:', error)
            } finally {
                this.loading = false
            }
        },

        filtrarPorRol(datos) {
            const session = useSessionStore()
            let filtrados = datos.filter(l => !LINEAS_EXCLUIDAS.includes(l.linea))

            if (!session.user) return filtrados

            const rol = session.user.rol?.toLowerCase()
            const userLinea = parseInt(session.user.linea_id)

            // Filtrar si es inspector O si es técnico con línea asignada
            if (rol === 'inspector' || (rol === 'tecnico' && !isNaN(userLinea))) {
                if (!isNaN(userLinea)) {
                    filtrados = filtrados.filter(l => parseInt(l.linea) === userLinea)
                } else if (rol === 'inspector') {
                    // Un inspector sin línea no ve nada por seguridad
                    filtrados = []
                }
            }

            return filtrados
        },

        verificarEstadoGps() {
            const now = Math.floor(Date.now() / 1000) - (new Date().getTimezoneOffset() * 60)
            const UMBRAL_ALERTA = 1800 // 30 minutos
            const fallasEncontradas = []

            this.datosMaquinas.forEach(linea => {
                linea.maquinas.forEach(maquina => {
                    if ((now - maquina.timestamp) > UMBRAL_ALERTA) {
                        fallasEncontradas.push({
                            idUnico: `${linea.linea}-${maquina.Maquina}`,
                            maquina: maquina.Maquina,
                            linea: linea.linea
                        })
                    }
                })
            })

            this.maquinasSinGps = fallasEncontradas
        }
    }
})
